import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'https://fiifit.ro';

export interface Challenge {
  id: string;
  title: string;
  description: string;
  type: string;
  difficulty: string;
  targetCount: number;
  targetType: string;
  pointsReward: number;
  badgeIcon: string;
  badgeName: string;
  lifestyleMode: string | null;
}

export interface UserChallenge {
  id: string;
  userId: string;
  challengeId: string;
  progress: number;
  isCompleted: boolean;
  startedAt: Date;
  completedAt: Date | null;
  challenge: Challenge;
}

export interface ChallengeStats {
  totalPoints: number;
  badges: Array<{
    badgeIcon: string;
    badgeName: string;
    earnedAt: Date;
  }>;
  badgeCount: number;
}

class ChallengesService {
  private getAuthHeader() {
    const token = localStorage.getItem('accessToken');
    return { Authorization: `Bearer ${token}` };
  }

  // Get all available challenges
  async getAllChallenges(): Promise<Challenge[]> {
    const response = await axios.get(`${API_URL}/api/challenges`, {
      headers: this.getAuthHeader(),
    });
    return response.data;
  }

  // Get user's enrolled challenges
  async getMyChallenges(): Promise<UserChallenge[]> {
    const response = await axios.get(`${API_URL}/api/challenges/my`, {
      headers: this.getAuthHeader(),
    });
    return response.data;
  }

  // Enroll in a challenge
  async enrollChallenge(challengeId: string): Promise<UserChallenge> {
    const response = await axios.post(
      `${API_URL}/api/challenges/${challengeId}/enroll`,
      {},
      { headers: this.getAuthHeader() }
    );
    return response.data;
  }

  // Update challenge progress
  async updateProgress(userChallengeId: string, increment: number = 1): Promise<UserChallenge> {
    const response = await axios.put(
      `${API_URL}/api/challenges/user/${userChallengeId}/progress`,
      { increment },
      { headers: this.getAuthHeader() }
    );
    return response.data;
  }

  // Get user stats (points & badges)
  async getStats(): Promise<ChallengeStats> {
    const response = await axios.get(`${API_URL}/api/challenges/stats`, {
      headers: this.getAuthHeader(),
    });
    return response.data;
  }
}

export const challengesService = new ChallengesService();
